import { UISimulation } from '@/components/help/UISimulation';
import { SimShell } from './simShell';

const D = 1900;

// Managing your sites — 4 frames: lobby -> open a site -> overview tabs ->
// health strip + metrics.
function SMLobby(): JSX.Element {
  return (
    <SimShell step={1} total={4} title="Sites lobby">
      <ul className="space-y-1.5 text-xs">
        {[
          ['naltrexone-guide', 'active'],
          ['runners-fuel', 'active'],
          ['ledger-basics', 'scaffolding'],
        ].map(([name, status]) => (
          <li
            key={name}
            className="flex items-center justify-between rounded border border-dash-border bg-dash-surface px-3 py-1.5"
          >
            <span className="text-dash-primary">{name}</span>
            <span
              className={
                'font-mono text-[10px] uppercase ' +
                (status === 'active' ? 'text-success' : 'text-warning')
              }
            >
              {status}
            </span>
          </li>
        ))}
      </ul>
    </SimShell>
  );
}

function SMOpen(): JSX.Element {
  return (
    <SimShell step={2} total={4} title="Open a site">
      <div className="rounded border border-accent/40 bg-accent/10 px-3 py-2 text-xs">
        <div className="text-dash-primary text-sm">naltrexone-guide</div>
        <div className="text-[10px] font-mono uppercase text-accent mt-0.5">
          opening site hub…
        </div>
      </div>
    </SimShell>
  );
}

function SMTabs(): JSX.Element {
  return (
    <SimShell step={3} total={4} title="Hub tabs">
      <div className="flex flex-wrap gap-1.5 text-[11px]">
        {['Overview', 'Domains', 'Theme', 'Components', 'Assets', 'Prompts', 'Articles'].map(
          (t, i) => (
            <span
              key={t}
              className={
                'rounded px-2 py-0.5 border ' +
                (i === 0
                  ? 'border-accent/40 bg-accent/10 text-accent'
                  : 'border-dash-border text-dash-secondary')
              }
            >
              {t}
            </span>
          ),
        )}
      </div>
    </SimShell>
  );
}

function SMHealth(): JSX.Element {
  return (
    <SimShell step={4} total={4} title="Overview dashboard">
      <div className="grid grid-cols-2 gap-1.5 text-[11px]">
        {[
          ['Integrations', 'ok', 'text-success'],
          ['DNS', 'live', 'text-success'],
          ['SSL', 'active', 'text-success'],
          ['Last deploy', '2m ago', 'text-dash-secondary'],
        ].map(([label, value, cls]) => (
          <div
            key={label}
            className="rounded border border-dash-border bg-dash-surface px-2 py-1.5"
          >
            <div className="text-dash-muted">{label}</div>
            <div className={'font-mono ' + cls}>{value}</div>
          </div>
        ))}
      </div>
    </SimShell>
  );
}

export function SiteManagementSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <SMLobby />, label: 'Every site in one lobby', duration: D },
        { frame: <SMOpen />, label: 'Open a site hub', duration: D },
        { frame: <SMTabs />, label: 'Tabs for every facet', duration: D },
        { frame: <SMHealth />, label: 'Overview health strip', duration: D },
      ]}
    />
  );
}

// Multi-site navigation — 3 frames: workspace switcher -> Cmd+K -> rail +
// workspace split.
function MSBreadcrumb(): JSX.Element {
  return (
    <SimShell step={1} total={3} title="Workspace switcher">
      <div className="rounded border border-dash-border bg-dash-surface px-2 py-1.5 text-xs max-w-[200px]">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <span className="text-dash-primary">naltrexone-guide</span>
            <div className="text-[10px] font-mono text-dash-muted">/naltrexone-guide</div>
          </div>
          <span aria-hidden="true" className="text-dash-muted">
            ▾
          </span>
        </div>
      </div>
      <div className="mt-1.5 text-[10px] font-mono uppercase text-dash-muted">
        atop the workspace column
      </div>
    </SimShell>
  );
}

function MSCmdK(): JSX.Element {
  return (
    <SimShell step={2} total={3} title="Cmd+K switcher">
      <div className="rounded border border-dash-border bg-dash-surface p-2 text-xs">
        <div className="rounded border border-dash-border bg-dash-bg px-2 py-1 text-dash-muted">
          search sites…
        </div>
        <ul className="mt-1.5 space-y-1">
          {['naltrexone-guide', 'runners-fuel'].map((s, i) => (
            <li
              key={s}
              className={
                'rounded px-2 py-1 ' +
                (i === 0 ? 'bg-accent/10 text-accent' : 'text-dash-secondary')
              }
            >
              {s}
            </li>
          ))}
        </ul>
      </div>
    </SimShell>
  );
}

function MSSidebar(): JSX.Element {
  return (
    <SimShell step={3} total={3} title="Rail + workspace">
      <div className="grid grid-cols-[auto_1fr] gap-2 text-[11px]">
        <div className="rounded border border-dash-border bg-dash-surface p-2 text-center">
          <div className="text-[9px] font-mono uppercase text-dash-muted mb-1">Rail</div>
          <div className="text-dash-secondary leading-5">
            Sites
            <br />
            Alerts
            <br />
            Settings
          </div>
        </div>
        <div className="rounded border border-dash-border bg-dash-surface p-2">
          <div className="text-[10px] font-mono uppercase text-dash-muted mb-1">Workspace</div>
          <div className="text-dash-secondary leading-5">
            switcher · Hub · Brainstorm · Pipeline · Drafts · Articles
          </div>
        </div>
      </div>
    </SimShell>
  );
}

export function MultiSiteNavSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <MSBreadcrumb />, label: 'Switch from the workspace', duration: D },
        { frame: <MSCmdK />, label: 'Or hit Cmd+K anywhere', duration: D },
        { frame: <MSSidebar />, label: 'Platform rail + site workspace', duration: D },
      ]}
    />
  );
}

// Site hub dashboard — 4 frames: health -> metrics -> recent activity -> quick
// actions.
function SHHealth(): JSX.Element {
  return (
    <SimShell step={1} total={4} title="Health strip">
      <div className="flex flex-wrap gap-1.5 text-[11px]">
        {['Integrations ok', 'DNS live', 'SSL active', 'Deploy 2m ago'].map((t) => (
          <span
            key={t}
            className="rounded border border-success/40 bg-success/10 text-success px-2 py-0.5 font-mono"
          >
            {t}
          </span>
        ))}
      </div>
    </SimShell>
  );
}

function SHMetrics(): JSX.Element {
  return (
    <SimShell step={2} total={4} title="Metric cards">
      <div className="grid grid-cols-2 gap-1.5 text-[11px]">
        {[
          ['Impressions', '12.4k', 'text-purple'],
          ['Clicks', '880', 'text-success'],
          ['CTR', '7.1%', 'text-accent'],
          ['Published', '24', 'text-dash-primary'],
        ].map(([label, value, cls]) => (
          <div
            key={label}
            className="rounded border border-dash-border bg-dash-surface px-2 py-1.5"
          >
            <div className="text-dash-muted">{label}</div>
            <div className={'font-mono text-sm ' + cls}>{value}</div>
          </div>
        ))}
      </div>
    </SimShell>
  );
}

function SHActivity(): JSX.Element {
  return (
    <SimShell step={3} total={4} title="Recent activity">
      <ul className="space-y-1 text-[11px] text-dash-secondary">
        <li>Published — Protein timing for runners</li>
        <li>Draft ready — Carb loading basics</li>
        <li>Run completed — score 8.7</li>
      </ul>
    </SimShell>
  );
}

function SHActions(): JSX.Element {
  return (
    <SimShell step={4} total={4} title="Quick actions">
      <div className="flex flex-wrap gap-1.5 text-[11px]">
        {['Brainstorm', 'Review drafts', 'Analytics', 'Export ZIP'].map((t) => (
          <span
            key={t}
            className="rounded border border-accent/40 bg-accent/10 text-accent px-2 py-0.5"
          >
            {t}
          </span>
        ))}
      </div>
    </SimShell>
  );
}

export function SiteHubSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <SHHealth />, label: 'Health at a glance', duration: D },
        { frame: <SHMetrics />, label: 'Search Console metrics', duration: D },
        { frame: <SHActivity />, label: 'Recent pipeline + publishes', duration: D },
        { frame: <SHActions />, label: 'Jump straight to work', duration: D },
      ]}
    />
  );
}

// Finding content — 3 frames: search bar -> type -> results filter.
function FCBar(): JSX.Element {
  return (
    <SimShell step={1} total={3} title="Search + filters">
      <div className="flex items-center gap-2 text-xs">
        <div className="flex-1 rounded border border-dash-border bg-dash-surface px-2 py-1 text-dash-muted">
          search drafts…
        </div>
        <span className="rounded border border-dash-border px-2 py-1 text-dash-secondary">
          status: all
        </span>
      </div>
    </SimShell>
  );
}

function FCType(): JSX.Element {
  return (
    <SimShell step={2} total={3} title="Type to filter">
      <div className="flex items-center gap-2 text-xs">
        <div className="flex-1 rounded border border-accent/40 bg-dash-surface px-2 py-1 text-dash-primary">
          protein
        </div>
        <span className="rounded border border-accent/40 bg-accent/10 text-accent px-2 py-1">
          status: draft
        </span>
      </div>
    </SimShell>
  );
}

function FCResults(): JSX.Element {
  return (
    <SimShell step={3} total={3} title="Filtered results">
      <ul className="space-y-1 text-[11px]">
        {['Protein timing for runners', 'Plant protein that hits 20g'].map((t) => (
          <li
            key={t}
            className="rounded border border-dash-border bg-dash-surface px-3 py-1.5 text-dash-primary"
          >
            {t}
          </li>
        ))}
      </ul>
      <div className="mt-1.5 text-[10px] font-mono text-dash-muted">
        URL holds ?q=protein&amp;status=draft
      </div>
    </SimShell>
  );
}

export function SearchFilterSortSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <FCBar />, label: 'Every list page has a search bar', duration: D },
        { frame: <FCType />, label: 'Type and pick a status', duration: D },
        { frame: <FCResults />, label: 'The URL remembers your filters', duration: D },
      ]}
    />
  );
}
